import { get, has, isArray, isEmpty } from 'lodash';
import { FormQuestion, FormQuestionOption } from '../../../../../../../Utils/Types/FormBuilder';
import { Locales } from '../../../../../../../Utils/Types';

export const convertQuestionOptionIdsToArray = (answer: any) => {
  if (has(answer, 'question_option_ids')) {
    if (!isArray(answer.question_option_ids)) {
      // Filter out null/undefined before wrapping in array, convert strings to ints
      const singleValue = answer.question_option_ids;
      const numValue = singleValue != null ? parseInt(singleValue, 10) : null;
      return {
        ...answer,
        question_option_ids: numValue != null && !isNaN(numValue) ? [numValue] : [],
      };
    }
    // Filter out null/undefined values from array, convert strings to ints
    return {
      ...answer,
      question_option_ids: answer.question_option_ids
        .map((id: any) => (typeof id === 'string' ? parseInt(id, 10) : id))
        .filter((id: any) => id != null && !isNaN(id)),
    };
  }

  return answer;
};

export const convertAttachmentsObjectsToArray = (answer: any) => {
  if (has(answer, 'attachments') && isArray(answer?.attachments)) {
    return {
      ...answer,
      attachments_attachments_attributes: answer.attachments.map((attachment: any) => ({
        id: attachment.id,
        blob_id: attachment.blob_id,

        _destroy: attachment._destroy,
      })),
    };
  }

  return answer;
};

export const filterQuestionOptionIds = (answer: any, questions: any) => {
  const emptyContentI18n = Locales.every((locale) =>
    isEmpty(get(answer, `content_i18n_${locale.value}`)),
  );

  if (!isEmpty(get(answer, 'question_option_ids')) && emptyContentI18n) {
    // remove question_option_ids that belongs to a question_option «other» if answer content is null
    const filteredIds = answer.question_option_ids.filter((questionOptionId: any) => {
      const question = questions?.find((q: any) =>
        q.question_options?.some((option: any) => option.id === questionOptionId),
      );
      const questionOption = question?.question_options?.find(
        (option: any) => option.id === questionOptionId,
      );
      return !questionOption?.other;
    });

    return { ...answer, question_option_ids: filteredIds };
  }

  return answer;
};

export const formDataProcessor = (data: any, questions: FormQuestion[]) => {
  const transformedData = {
    ...data,

    answers_attributes: data.answers_attributes
      .filter((answer: any) => !answer.hidden) // filter out hidden fields (sections)
      .map(convertQuestionOptionIdsToArray)
      .map(convertAttachmentsObjectsToArray)
      .map((answer: any) => filterQuestionOptionIds(answer, questions))
      .map((answer: any) => {
        // Remove empty id field to prevent Rails uniqueness validation errors
        if (answer.id === '' || answer.id === null || answer.id === undefined) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { id, ...rest } = answer;
          return rest;
        }
        return answer;
      }),
  };
  return transformedData;
};

export const submitForm = ({ data, setLoading, submitFormResponse, questions }: any) => {
  setLoading(true);

  submitFormResponse(formDataProcessor(data, questions));
};

export function transformKeys(obj: any) {
  const newObj: any = {};
  Object.keys(obj).forEach((key) => {
    // Replace and reformat the key for answers_attributes
    const newKey = key.replace(/answers\[(\d+)\]/, 'answers_attributes.$1');

    newObj[newKey] = obj[key];
  });
  return newObj;
}

export function remapCheckBoxesValues(
  value: any,
  isChecked: any,
  questionOption: any,
  onChange: (...event: any[]) => void,
) {
  const valueFallback = value || [];
  const newValues = isChecked
    ? valueFallback.filter((id: string) => id !== questionOption.id)
    : [...valueFallback, questionOption.id];
  onChange(newValues);
}

export function checkOtherCheckbox(
  question: FormQuestion,
  questionOptionsIdsController: any,
  answerAttributes: any,
) {
  const questionOptionsIds = question.question_options
    ?.filter((questionOption: FormQuestionOption) => questionOption.other)
    .map((questionOption: FormQuestionOption) => questionOption.id);

  const uniqueIds = [...(answerAttributes.question_option_ids || []), ...questionOptionsIds].filter(
    (value: any, index: any, self: any) => self.indexOf(value) === index,
  );

  questionOptionsIdsController.field.onChange(uniqueIds);
}
